import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useConferma } from "@/hooks/use-conferma";
import { supabase } from "@/lib/supabase";
import { annullaTotp, confermaTotp, iscriviTotp } from "@/lib/totp";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CodiceSei } from "@/components/auth/CampiAccesso";

/**
 * «Il tuo accesso»: il profilo e il codice di verifica di chi è entrato, per ogni ruolo (blocco 4 del pacchetto).
 * Stanno in Impostazioni › Il tuo accesso e, per chi non ha le Impostazioni (il venditore, il montatore), nella
 * pagina `/sicurezza` che apre il menu col nome. Qui non passa nessun dato di business: solo ciò che Supabase
 * lascia gestire alla persona stessa (e togliere un fattore verificato vuole la sessione col codice dato).
 */
const RUOLI = { titolare: "Titolare", amministrazione: "Amministrazione", venditore: "Venditore", montatore: "Montatore" };

export function ProfiloCard() {
  const { profile } = useAuth();
  const righe = [
    ["Nome", profile?.nome ? `${profile.nome} ${profile.cognome || ""}`.trim() : "—"],
    ["Email", profile?.email || "—"],
    ["Ruolo", RUOLI[profile?.ruolo] || profile?.ruolo || "—"],
  ];
  return (
    <section data-testid="impostazioni-profilo" className="grid gap-group">
      <div>
        <h2 className="text-card-title text-ink-strong">Profilo</h2>
        <p className="mt-micro text-ink-2">I tuoi dati nel gestionale. Per cambiarli, scrivi al titolare.</p>
      </div>
      <Card className="max-w-2xl p-card">
        <dl className="grid gap-inside sm:grid-cols-[120px_minmax(0,1fr)]">
          {righe.map(([etichetta, valore]) => (
            <div key={etichetta} className="contents">
              <dt className="text-ink-2">{etichetta}</dt>
              <dd className="min-w-0 break-words font-semibold text-ink">{valore}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </section>
  );
}

const quando = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const stessoAnno = d.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", ...(stessoAnno ? {} : { year: "numeric" }) }).format(d);
};

export function SicurezzaCard() {
  const { profile, factors, refreshAal } = useAuth();
  const { chiedi, dialogoConferma } = useConferma();
  const titolare = profile?.ruolo === "titolare";
  const fattore = (factors?.totp || []).find((f) => f.status === "verified");
  const attivo = Boolean(fattore);
  const dal = quando(fattore?.created_at);

  const [iscrizione, setIscrizione] = useState(null); // { id, totp: { qr_code, secret } }
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);
  const [codice, setCodice] = useState("");
  const [busy, setBusy] = useState(false);

  const attiva = async () => {
    setErrore(null);
    setInCorso(true);
    setBusy(true);
    try {
      const { data, error } = await iscriviTotp();
      if (error) {
        setErrore(error.message || "Il QR non è arrivato.");
        return;
      }
      setIscrizione(data);
    } catch (e) {
      setErrore((e && e.message) || "Il QR non è arrivato.");
    } finally {
      setBusy(false);
    }
  };

  const lascia = async () => {
    if (iscrizione) await annullaTotp(iscrizione.id);
    setIscrizione(null);
    setInCorso(false);
    setErrore(null);
    setCodice("");
  };

  const conferma = async () => {
    if (!iscrizione) return;
    setBusy(true);
    const { error, codiceSbagliato } = await confermaTotp(iscrizione.id, codice);
    setBusy(false);
    if (error) {
      toast.error(
        codiceSbagliato
          ? "Il codice non torna. Scrivi quello che l'app mostra adesso."
          : `Non riesco a controllare il codice: ${error.message}`,
      );
      setCodice("");
      return;
    }
    setIscrizione(null);
    setInCorso(false);
    setCodice("");
    await refreshAal();
    toast.success("Codice di verifica attivo. Dal prossimo accesso, dopo la password, ti chiediamo il codice.");
  };

  /* Per il titolare il codice è obbligatorio: «toglierlo» vuol dire spostarlo. Il flusso è uno solo: si toglie
     il codice di adesso e il cancello chiede subito di collegare il telefono nuovo (TotpEnrollmentPage). */
  const togli = async () => {
    const ok = await chiedi({
      titolo: titolare ? "Spostare il codice su un altro telefono?" : "Togliere il codice di verifica?",
      descrizione: titolare
        ? "Il codice del telefono di adesso smette di valere subito e ti chiediamo di collegare quello nuovo. Tieni il telefono nuovo in mano."
        : "Da quel momento per entrare basta la password. Qui dentro ci sono i dati dei clienti.",
      verbo: titolare ? "Sposta il codice" : "Togli il codice di verifica",
    });
    if (!ok) return;
    setBusy(true);
    let sbaglio = null;
    for (const f of factors?.totp || []) {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: f.id });
      if (error) sbaglio = error;
    }
    await refreshAal();
    setBusy(false);
    if (sbaglio) toast.error(`Non sono riuscito a toglierlo: ${sbaglio.message}`);
    else toast.success(titolare ? "Codice tolto dal telefono vecchio" : "Codice di verifica tolto");
  };

  return (
    <section data-testid="impostazioni-sicurezza" className="grid gap-group">
      {dialogoConferma}
      <div>
        <h2 className="text-card-title text-ink-strong">Sicurezza</h2>
        <p className="mt-micro max-w-xl text-ink-2">
          Oltre alla password, un codice di 6 cifre che cambia ogni 30 secondi sul tuo telefono.
        </p>
      </div>

      <Card className="max-w-2xl p-card">
        <div className="flex gap-group">
          <span aria-hidden="true" className="hidden size-12 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand sm:inline-flex">
            <ShieldCheck className="size-6" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-inside">
              <h3 className="text-h4 text-ink-strong">Codice di verifica</h3>
              <StatusBadge tone={attivo ? "success" : "neutral"} data-testid="sicurezza-stato">
                {attivo ? "Attivo" : "Non attivo"}
              </StatusBadge>
            </div>
            <p className="mt-micro text-ink-2">
              {attivo
                ? `${dal ? `Attivo dal ${dal} · ` : ""}app di autenticazione sul telefono`
                : "Non ancora attivo su questo account."}
            </p>
            <p className="mt-micro max-w-xl text-ink-2">
              {titolare
                ? attivo
                  ? "Per il titolare è obbligatorio: si può spostare su un altro telefono, non togliere."
                  : "Per il titolare è obbligatorio."
                : "Facoltativo. Se qualcuno scopre la tua password, senza il tuo telefono non entra."}
            </p>

            {attivo ? (
              <div className="mt-card">
                <Button variant="secondary" onClick={togli} disabled={busy} data-testid="btn-togli-totp" className="max-md:w-full">
                  {titolare ? "Spostalo su un altro telefono" : "Togli il codice di verifica"}
                </Button>
              </div>
            ) : !inCorso ? (
              <div className="mt-card">
                <Button onClick={attiva} disabled={busy} data-testid="btn-attiva-totp" className="max-md:w-full">
                  Attiva il codice di verifica
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {!attivo && inCorso ? (
          <div className="mt-card border-t border-line-subtle pt-card" data-testid="sicurezza-attivazione">
            <ol className="grid gap-card">
              <li>
                <p className="font-semibold text-ink">1. Inquadra il QR con l&apos;app di autenticazione</p>
                <p className="mt-micro text-small text-ink-2">Google Authenticator, 1Password o Authy: una qualsiasi. La voce si chiamerà Interni D&apos;Autore.</p>
                <div className="mt-inside flex justify-center rounded-lg border border-line bg-surface p-group sm:w-fit">
                  {iscrizione?.totp?.qr_code ? (
                    <img data-testid="totp-qr-code" src={iscrizione.totp.qr_code} alt="QR del codice di verifica" className="size-44" />
                  ) : errore ? (
                    <div role="alert" data-testid="totp-enroll-error" className="flex w-full gap-inside text-danger-ink">
                      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                      <div className="min-w-0">
                        <p className="font-semibold">Il QR non è arrivato.</p>
                        <p className="mt-micro break-words text-small">{errore}</p>
                        <Button type="button" variant="secondary" size="sm" className="mt-inside" onClick={attiva} disabled={busy}>
                          Riprova
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex size-44 items-center justify-center text-small text-ink-2">Preparo il QR…</div>
                  )}
                </div>
                {iscrizione?.totp?.secret ? (
                  <div className="mt-inside">
                    <p className="text-small text-ink-2">Se non riesci a inquadrarlo, scrivi a mano la chiave:</p>
                    <p data-testid="totp-secret-key" className="mt-micro select-all break-all font-mono text-ink">
                      {iscrizione.totp.secret}
                    </p>
                  </div>
                ) : null}
              </li>
              <li>
                <p className="font-semibold text-ink">2. Scrivi il codice che l&apos;app ti mostra</p>
                <div className="mt-inside flex" data-testid="totp-code-input">
                  <CodiceSei value={codice} onChange={setCodice} disabled={!iscrizione} aria-label="Codice a 6 cifre dell'app di autenticazione" />
                </div>
              </li>
            </ol>
            <div className="mt-card flex flex-col-reverse gap-inside sm:flex-row sm:justify-end">
              <Button variant="ghost" onClick={lascia} disabled={busy} data-testid="btn-annulla-totp">
                Lascia perdere
              </Button>
              <Button onClick={conferma} disabled={busy || !iscrizione || codice.length < 6} data-testid="btn-conferma-totp">
                {busy && iscrizione ? "Controllo…" : "Attiva"}
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      {titolare ? (
        <Card className="max-w-2xl p-card">
          <h3 className="text-h4 text-ink-strong">E i collaboratori?</h3>
          <p className="mt-micro text-ink-2">
            Ognuno attiva il proprio codice da qui, anche chi non vede le altre impostazioni. Per loro non è obbligatorio.
          </p>
        </Card>
      ) : null}
    </section>
  );
}
